import { Alert, Box, Button, CircularProgress, Grid } from '@mui/material';
import { AnimatePresence, m } from 'framer-motion';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Navigate, useMatch, useSearch } from 'react-location';
import AuthNavbar from 'src/components/AuthNavbar';
import CheckoutFailure from 'src/components/CheckoutFailure';
import useAcceptLegalAgreements from 'src/hooks/core-api/useAcceptLegalAgreements';
import useCreatePaymentDetailsPortal from 'src/hooks/core-api/useCreatePaymentDetailsPortal';
import useLegalAgreements from 'src/hooks/core-api/useLegalAgreements';
import { LocationGenerics } from 'src/routes';
import OnboardingCard from 'src/sections/onboarding/OnboardingCard';
import {
  AgreementsStep,
  CheckoutDetailsDescription,
  DetailsDescriptionStep,
  WelcomeStep,
} from 'src/sections/onboarding/steps';
import { variants } from './OnboardingPage.animations';
import { onboardingCardSizing } from './OnboardingPage.styles';

interface OnboardingStepProps {
  onNextStep: () => void;
}

const OnboardingPage = () => {
  const {
    data: { user },
  } = useMatch<LocationGenerics>();

  const legalAgreementsQuery = useLegalAgreements();
  const acceptLegalAgreementsMutation = useAcceptLegalAgreements();
  const createPaymentDetailsPortalMutation = useCreatePaymentDetailsPortal();
  const { enqueueSnackbar } = useSnackbar();

  const search = useSearch();
  const paymentFailure = search.payment_failed;
  const [[currentStep, direction], setCurrentStep] = useState([0, 0]);
  const backgroundColor = '#F6F7F9';

  // Page guard
  if (user?.onboarding_status !== 'payment_information_required') {
    return <Navigate to={`/dashboard`} />;
  }

  const paginate = (newDirection: number) => {
    setCurrentStep([currentStep + newDirection, newDirection]);
  };

  const handleNextStep = () => {
    const stepsLength = steps.length;
    if (currentStep + 1 < stepsLength) {
      paginate(1);
    } else {
      alert('All steps complete!');
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      paginate(-1);
    }
  };

  const handleCheckout = () => {
    createPaymentDetailsPortalMutation.mutate(
      {},
      {
        onSuccess: (data) => window.location.replace(data.portal_url),
        onError: (error) => {
          const errorMessage = `Failed to load payment details portal`;
          enqueueSnackbar(errorMessage, {
            variant: 'error',
          });
        },
      }
    );
  };

  const handleAcceptLegalAgreements = () => {
    acceptLegalAgreementsMutation.mutate(
      {},
      {
        onSuccess: () => handleNextStep(),
        onError: (error) => {
          const errorMessage = `Failed to accept legal terms`;
          enqueueSnackbar(errorMessage, {
            variant: 'error',
          });
        },
      }
    );
  };

  const LegalAgreements = () => {
    const marginTop = 2;
    if (legalAgreementsQuery.isLoading || legalAgreementsQuery.isIdle) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: marginTop }}>
          <CircularProgress />
        </Box>
      );
    }

    if (legalAgreementsQuery.isError) {
      return (
        <Alert
          sx={{ mt: marginTop }}
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => legalAgreementsQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Error loading legal agreements...
        </Alert>
      );
    }

    return (
      <AgreementsStep
        agreements={legalAgreementsQuery.data}
        onNextStep={handleAcceptLegalAgreements}
        isLoading={acceptLegalAgreementsMutation.isLoading}
      />
    );
  };

  const steps: React.FC<OnboardingStepProps>[] = [
    () => <WelcomeStep name={user?.first_name ?? 'New User'} onNextStep={handleNextStep} />,
    () => <DetailsDescriptionStep onNextStep={handleNextStep} />,
    LegalAgreements,
    () => (
      <CheckoutDetailsDescription
        onNextStep={handleCheckout}
        isLoading={createPaymentDetailsPortalMutation.isLoading}
      />
    ),
  ];

  let CurrentStepComponent =
    paymentFailure === 'true'
      ? () => (
          <CheckoutFailure
            onRetry={handleCheckout}
            isLoading={createPaymentDetailsPortalMutation.isLoading}
          />
        )
      : steps[currentStep];

  return (
    <div style={{ background: backgroundColor }}>
      <AuthNavbar />
      <Grid
        sx={{
          height: '100vh',
          pt: { xs: 2, sm: 0 },
        }}
        container
        justifyContent="center"
        alignItems="center"
      >
        <OnboardingCard
          displayBackButton={currentStep > 0}
          onPreviousStep={handlePreviousStep}
          sx={onboardingCardSizing}
        >
          <AnimatePresence initial={false} custom={direction}>
            <m.div
              key={currentStep}
              style={{ height: '100%' }}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
            >
              <CurrentStepComponent onNextStep={handleNextStep} />
            </m.div>
          </AnimatePresence>
        </OnboardingCard>
      </Grid>
    </div>
  );
};

export type { OnboardingStepProps };
export default OnboardingPage;
